import React, { useState } from "react";
import { App, Button, Form, Input, Select, Space } from "antd";
import { ProCard } from "@ant-design/pro-components";
import { ArrowLeftOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  Block,
  EmailStyles,
  LETTER_LANGUAGES,
  LetterLanguage,
} from "../blocks";
import { AUDIENCES, DEFAULT_SENDER } from "../data";
import LetterBlock from "../Builder/LetterBlock";
import LetterSheet, { Device } from "../Builder/LetterSheet";
import SaveDraftDialog from "../SaveDraftDialog";
import PageHead from "../../../Common/PageHead";
import ViewSwitches from "../ViewSwitches";
import PanelTitle from "../Builder/PanelTitle";
import { useScrolledUnder } from "../../../Common/StickyBar";

import styles from "./index.module.scss";

interface SendProps {
  blocks: Block[];
  emailStyles: EmailStyles;
  onBack: () => void;
  /** Leaves the flow: the campaign is on its way and there is nothing left to
   * do here. */
  onSent: () => void;
  /** What the campaign is already called, where it is called anything. */
  subject: string;
  /** Who it goes to, where the campaign it came from named someone. */
  audience?: string;
  /** Leaves the flow with the letter kept as a draft campaign. */
  onSaveDraft: () => void;
  /**
   * Whether this step holds work that has not been saved. The sending details
   * are part of the campaign, so filling them in is something to lose — the
   * section asks before leaving once anything here has been touched.
   */
  onDirty: (dirty: boolean) => void;
}

/**
 * The last step before a campaign leaves: the letter as it will arrive, beside
 * the details it is sent with.
 *
 * The letter is the builder's own — same sections, same sheet, same widths —
 * but read-only: nothing here selects, drags or edits, because this screen is
 * for checking rather than changing.
 *
 * TODO(design): every field on this screen is local state. A developer needs
 * the campaign's own record behind them — subject, sender and audience — and an
 * endpoint to send with. See DEVELOPER_TASKS.md.
 */
export default function Send({
  blocks,
  emailStyles,
  subject: named,
  audience: sendTo,
  onBack,
  onSent,
  onSaveDraft,
  onDirty,
}: SendProps) {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const previewScroll = useScrolledUnder();

  const [device, setDevice] = useState<Device>("desktop");
  const [language, setLanguage] = useState<LetterLanguage>(LETTER_LANGUAGES[0]);
  // Carried in from the builder, which had it from the campaign being edited.
  const [subject, setSubject] = useState(named);
  // Filled in from the account rather than left blank: the sender is the same
  // on almost every campaign, and it stays editable for the one where it is not.
  const [fromName, setFromName] = useState(DEFAULT_SENDER.name);
  const [replyTo, setReplyTo] = useState(DEFAULT_SENDER.replyTo);
  // The campaign's own list where it came from one, the first otherwise.
  const [audience, setAudience] = useState(sendTo ?? AUDIENCES[0].id);
  const [testTo, setTestTo] = useState("");

  /**
   * A setter that also reports the step as unsaved. Every field of the campaign
   * goes through it; the test address does not, since it is typed to try
   * something and belongs to nobody once the screen is left.
   */
  const edit =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      onDirty(true);
    };
  // Open only when Save as draft is pressed with no subject typed.
  const [naming, setNaming] = useState(false);

  const list = AUDIENCES.find((item) => item.id === audience) ?? AUDIENCES[0];
  // What the marks above promise: the button is not live until they are filled.
  const ready =
    subject.trim() !== "" && fromName.trim() !== "" && replyTo.trim() !== "";
  const recipients = list.recipients.toLocaleString();

  // Sending cannot be taken back, so it is asked before it is done — the same
  // way replacing a letter is.
  const confirmSend = () =>
    modal.confirm({
      title: t("pages.campaigns.send.confirmTitle"),
      content: t("pages.campaigns.send.confirmText", {
        count: list.recipients,
        recipients,
        audience: list.name,
      }),
      okText: t("pages.campaigns.send.confirmOk"),
      onOk: () => {
        message.success(
          t("pages.campaigns.send.sent", {
            count: list.recipients,
            recipients,
            audience: list.name,
          }),
        );
        onSent();
      },
    });

  return (
    <>
      <SaveDraftDialog
        open={naming}
        onCancel={() => setNaming(false)}
        onSaved={(named) => {
          setNaming(false);
          setSubject(named);
          onSaveDraft();
        }}
      />

      <PageHead title={t("pages.campaigns.send.title")}>
        <Space>
          {/* Never disabled: a draft is where unfinished work is put down, and
              asking for the sending details first would defeat that. The one
              thing it does need is a name — the list shows drafts by subject,
              so a nameless one is a draft nobody can find again. When the
              subject on this screen is empty, the same dialog the builder uses
              asks for it. */}
          <Button
            onClick={() => {
              const named = subject.trim();

              if (!named) {
                setNaming(true);

                return;
              }

              message.success(
                t("pages.campaigns.builder.draftSaved", { name: named }),
              );
              onSaveDraft();
            }}
          >
            {t("common.saveAsDraft")}
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            disabled={!ready}
            onClick={confirmSend}
          >
            {t("pages.campaigns.send.send")}
          </Button>
        </Space>
      </PageHead>

      <div className={clsx("transparent", "flush-bottom", styles.layout)}>
        {/* The letter, on the same sheet the builder shows it on. */}
        <ProCard className={styles.previewCard}>
          <div className={styles.toolbar} style={previewScroll.shadow}>
            {/* The same shape the builder's toolbar has: the way back at the
                start, the controls that act on the letter in the middle. */}
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
              {t("pages.campaigns.send.back")}
            </Button>

            <ViewSwitches
              device={device}
              onDevice={setDevice}
              language={language}
              onLanguage={setLanguage}
            />
          </div>

          <div className={styles.canvas} onScroll={previewScroll.onScroll}>
            <LetterSheet
              device={device}
              background={emailStyles.background}
              pageBackground={emailStyles.emailBackground}
              fontFamily={emailStyles.fontFamily}
            >
              {blocks.map((block) => (
                <LetterBlock
                  key={block.id}
                  block={block}
                  emailStyles={emailStyles}
                  // Read-only: preview turns off selection, dragging and the
                  // section toolbar, so the callbacks below are never reached.
                  preview
                  selected={false}
                  onSelect={() => undefined}
                  onMove={() => undefined}
                  onDuplicate={() => undefined}
                  onRemove={() => undefined}
                  canRemove={false}
                  canMoveUp={false}
                  canMoveDown={false}
                />
              ))}
            </LetterSheet>
          </div>
        </ProCard>

        {/* What the letter is sent as, and to whom. */}
        <ProCard className={styles.detailsCard}>
          {/* antd's Form, for the required marks alone: the asterisk, its
              placement and its colour are the library's, and hand-drawing one
              beside a plain label would be a second way to say the same thing.
              The fields stay controlled by this component — a Form.Item with no
              `name` renders the mark without taking over the value. */}
          {/* The heading sits in a bar of its own, the same height and with
              the same hairline under it as the preview's toolbar across the
              row — so the two cards start on one line. */}
          <div className={styles.detailsHead}>
            {/* The same heading the builder's three panels open with, so both
                halves of this step start at one size. */}
            <PanelTitle>{t("pages.campaigns.send.details")}</PanelTitle>
          </div>

          <Form layout="vertical" className={styles.details}>
            <Form.Item required label={t("pages.campaigns.send.subject")}>
              <Input
                value={subject}
                placeholder={t("pages.campaigns.send.subjectHint")}
                onChange={(event) => edit(setSubject)(event.target.value)}
              />
            </Form.Item>

            <Form.Item required label={t("pages.campaigns.send.fromName")}>
              <Input
                value={fromName}
                onChange={(event) => edit(setFromName)(event.target.value)}
              />
            </Form.Item>

            <Form.Item required label={t("pages.campaigns.send.replyTo")}>
              <Input
                type="email"
                value={replyTo}
                onChange={(event) => edit(setReplyTo)(event.target.value)}
              />
            </Form.Item>

            <Form.Item
              required
              label={t("pages.campaigns.send.audience")}
              extra={t("pages.campaigns.send.recipients", {
                count: list.recipients,
                recipients,
              })}
            >
              <Select
                value={audience}
                onChange={edit(setAudience)}
                options={AUDIENCES.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
              />
            </Form.Item>

            {/* A letter is worth seeing in a real inbox before it goes to
                thousands of them. Optional, and the only field here that is. */}
            <Form.Item label={t("pages.campaigns.send.test")}>
              {/* `block` is antd's own way to make a compact group fill its
                  row — no width of ours. */}
              <Space.Compact block>
                <Input
                  type="email"
                  value={testTo}
                  placeholder={t("pages.campaigns.send.testHint")}
                  onChange={(event) => setTestTo(event.target.value)}
                />
                <Button
                  disabled={!testTo}
                  onClick={() =>
                    message.success(
                      t("pages.campaigns.send.testSent", { email: testTo }),
                    )
                  }
                >
                  {t("pages.campaigns.send.sendTest")}
                </Button>
              </Space.Compact>
            </Form.Item>
          </Form>
        </ProCard>
      </div>
    </>
  );
}
